import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import React, { useId, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { Dialog } from "@sequentech/ui-essentials";
import { translate } from "@sequentech/ui-core";
import { useTranslation } from "react-i18next";
import { computeSlateChoices, SlateOverMaximumError, slateRemovesChoices, } from "../../services/SlateChoices";
import { getSlateName } from "../../services/Slates";
import { applySlateSelection, selectBallotSelectionByElectionId, } from "../../store/ballotSelections/ballotSelectionsSlice";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
export var SlateApplyAction = function (_a) {
    var _b, _c, _d, _e, _f, _g;
    var ballotStyle = _a.ballotStyle, slate = _a.slate;
    var _h = useTranslation(), t = _h.t, i18n = _h.i18n;
    var dispatch = useAppDispatch();
    var selection = useAppSelector(selectBallotSelectionByElectionId(ballotStyle.election_id));
    var _j = useState(), pending = _j[0], setPending = _j[1];
    var _k = useState(false), wasChosen = _k[0], setWasChosen = _k[1];
    var unavailableId = useId();
    var slateName = getSlateName(slate, i18n.language, (_d = (_c = (_b = ballotStyle.ballot_eml.election_presentation) === null || _b === void 0 ? void 0 : _b.language_conf) === null || _c === void 0 ? void 0 : _c.default_language_code) !== null && _d !== void 0 ? _d : (_f = (_e = ballotStyle.ballot_eml.election_event_presentation) === null || _e === void 0 ? void 0 : _e.language_conf) === null || _f === void 0 ? void 0 : _f.default_language_code);
    var _l = useMemo(function () {
        if (!selection) {
            return {};
        }
        try {
            return { choices: computeSlateChoices(slate, selection) };
        }
        catch (error) {
            console.log("Error computing slate choices: ".concat(error));
            return { problem: error };
        }
    }, [slate, selection]), choices = _l.choices, problem = _l.problem;
    var unavailableReason = useMemo(function () {
        var _a;
        if (!problem) {
            return null;
        }
        if (problem instanceof SlateOverMaximumError) {
            var _b = problem.slateContest, contest = _b.contest, candidates = _b.candidates;
            return t("slates.apply.overMaximum", {
                slate: slateName,
                contest: (_a = translate(contest, "name", i18n.language)) !== null && _a !== void 0 ? _a : contest.id,
                candidates: candidates.length,
                max: contest.max_votes,
            });
        }
        return t("slates.apply.unavailable", { slate: slateName });
    }, [problem, slateName, t, i18n.language]);
    var apply = function () {
        dispatch(applySlateSelection({ ballotStyle: ballotStyle, slate: slate }));
        setWasChosen(true);
    };
    var handleChoose = function () {
        if (!choices) {
            return;
        }
        if (slateRemovesChoices(choices)) {
            setPending(choices);
        }
        else {
            apply();
        }
    };
    var handleClose = function (confirmed) {
        setPending(undefined);
        if (confirmed) {
            apply();
        }
    };
    var candidateNames = function (candidates, ids) {
        return ids
            .map(function (id) {
            var _a;
            var candidate = candidates.find(function (entry) { return entry.id === id; });
            return candidate ? ((_a = translate(candidate, "name", i18n.language)) !== null && _a !== void 0 ? _a : id) : id;
        })
            .join(", ");
    };
    var isSelection = wasChosen && (choices === null || choices === void 0 ? void 0 : choices.changes.length) === 0;
    var candidatesCount = slate.contests.reduce(function (total, entry) { return total + entry.candidates.length; }, 0);
    return (_jsxs(Box, { className: "slate-apply", children: [_jsx(Button, { className: "slate-apply-button", variant: "secondary", onClick: handleChoose, disabled: !choices, "aria-label": t("slates.apply.buttonLabel", { slate: slateName }), "aria-describedby": unavailableReason ? unavailableId : undefined, children: t("slates.apply.button") }), _jsx(Typography, { className: "slate-apply-status", role: "status", variant: "body2", children: isSelection
                    ? t("slates.apply.chosen", {
                        slate: slateName,
                        candidates: candidatesCount,
                        contests: slate.contests.length,
                    })
                    : null }), unavailableReason && (_jsx(Typography, { className: "slate-apply-unavailable", id: unavailableId, variant: "body2", children: unavailableReason })), _jsxs(Dialog, { className: "slate-replace-dialog", open: !!pending, handleClose: handleClose, title: t("slates.apply.replaceDialog.title"), ok: t("slates.apply.replaceDialog.ok"), cancel: t("slates.apply.replaceDialog.cancel"), variant: "action", children: [_jsx(Typography, { className: "slate-replace-content", children: t("slates.apply.replaceDialog.content", { slate: slateName }) }), ((_g = pending === null || pending === void 0 ? void 0 : pending.changes) !== null && _g !== void 0 ? _g : [])
                        .filter(function (change) { return change.removed.length > 0; })
                        .map(function (change) {
                        var _a, _b;
                        var contest = (_a = slate.contests.find(function (entry) { return entry.contest.id === change.contestId; })) === null || _a === void 0 ? void 0 : _a.contest;
                        if (!contest) {
                            return null;
                        }
                        return (_jsxs(Box, { className: "slate-replace-contest", sx: { marginTop: 2 }, children: [_jsx(Typography, { className: "slate-replace-contest-name", variant: "h6", component: "h3", children: (_b = translate(contest, "name", i18n.language)) !== null && _b !== void 0 ? _b : contest.id }), _jsxs(Typography, { className: "slate-replace-removed", children: [t("slates.apply.replaceDialog.removed"), " ", _jsx("strong", { className: "slate-replace-candidates", children: candidateNames(contest.candidates, change.removed) })] }), change.added.length > 0 && (_jsxs(Typography, { className: "slate-replace-added", children: [t("slates.apply.replaceDialog.added"), " ", _jsx("strong", { className: "slate-replace-candidates", children: candidateNames(contest.candidates, change.added) })] }))] }, change.contestId));
                    })] })] }));
};
